/**
 * "Needs a decision" — the short list a planner should act on before anything
 * else on this screen. Not every open drop: only delivery legs where something
 * has gone wrong and won't fix itself (part-delivered with a balance, a POD
 * that was undone, or a consignment that keeps bouncing off the van).
 *
 * Collapsed by default (same pattern as Supporting records). Renders nothing
 * when the list is empty, so a clean day stays clean.
 */

import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import type { OrdersDailyRow } from '../../types/ordersDailyTruth';
import {
  daysSinceYmd,
  formatOrdersImpact,
  ordersDecisionKind,
  ordersDecisionReason,
  overdueLabel,
} from '../../utils/ordersDailyPresentation';

interface NeedsADecisionStripProps {
  rows: OrdersDailyRow[];
  loading?: boolean;
  /** Open the supporting-records table focused on this consignment. */
  onOpen: (trackingId: string) => void;
}

const KIND_LABEL: Record<NonNullable<ReturnType<typeof ordersDecisionKind>>, string> = {
  partial: 'Part-delivered',
  regressed: 'POD then re-opened',
  repeated_fail: 'Repeatedly failed',
};

export default function NeedsADecisionStrip({ rows, loading, onOpen }: NeedsADecisionStripProps) {
  const [open, setOpen] = useState(false);

  const items = rows
    .filter((r) => ordersDecisionKind(r) != null)
    .sort((a, b) => {
      const byAge = (daysSinceYmd(b.grain_date) ?? 0) - (daysSinceYmd(a.grain_date) ?? 0);
      if (byAge !== 0) return byAge;
      return (b.failed_attempts ?? 0) - (a.failed_attempts ?? 0);
    });

  if (loading || items.length === 0) return null;

  return (
    <section aria-label="Needs a decision">
      <Button
        variant="ghost"
        size="sm"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="needs-a-decision-panel"
        className="justify-start text-left text-xs font-semibold uppercase tracking-wide-3 text-warning/80 hover:text-warning mb-3"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <AlertTriangle className="w-3.5 h-3.5" />
        Needs a decision ({items.length})
        {!open && (
          <span className="normal-case tracking-normal font-normal text-warning/50">
            — open to act
          </span>
        )}
      </Button>
      {!open ? null : (
        <div id="needs-a-decision-panel" className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((row) => {
            const kind = ordersDecisionKind(row)!;
            const overdue = overdueLabel(row.grain_date);
            const impact = formatOrdersImpact(row);
            return (
              <Button
                variant="secondary"
                key={`${row.tracking_id}-${row.direction}`}
                type="button"
                onClick={() => onOpen(row.tracking_id)}
                className="flex-col items-stretch text-left rounded-lg border border-warning/40 bg-warning/5 px-4 py-3 hover:border-warning/70 focus-visible:ring-ring"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-semibold text-warning truncate">
                    {row.customer || row.consignment_no || row.tracking_id}
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wide text-warning/90 shrink-0">
                    {KIND_LABEL[kind]}
                  </span>
                </div>
                <p className="text-xs text-foreground mt-1">{ordersDecisionReason(row)}</p>
                <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
                  <span className="font-mono">{row.tracking_id}</span>
                  {overdue && <span className={overdue === 'due today' ? '' : 'text-warning/80'}>{overdue}</span>}
                  {impact !== '—' && <span className="text-destructive/80">{impact}</span>}
                </div>
              </Button>
            );
          })}
        </div>
      )}
    </section>
  );
}
